import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Users } from './CustomIcons';
import { useAuth } from '../context/AuthContext';
import ViewToggle, { ToggleSeparator, PROTO_TO_WEB_ROLE, ACTIVE_ROLE_KEY, PENDING_SWITCH_KEY } from './ViewToggle';

const ROLES = [
  { key: 'learner',          label: 'Learner',           desc: 'Jonathan Johnson' },
  { key: 'facility_admin',   label: 'Facility Admin',    desc: 'Dr. Sarah Mitchell' },
  { key: 'case_manager',     label: 'Case Manager',      desc: 'Lisa Park' },
  { key: 'instructor',       label: 'Instructor',        desc: 'Robert Hayes' },
  { key: 'employer_partner', label: 'Employer Partner',  desc: 'Mike Torres' },
  { key: 'provider_partner', label: 'Training Provider', desc: 'Dr. Angela Torres' },
  { key: 'youth_learner',    label: 'Youth Learner',     desc: 'Jordan Mercer' },
];

const ROLE_LABELS = {
  learner: 'Learner',
  facility_admin: 'Facility Admin',
  case_manager: 'Case Manager',
  instructor: 'Instructor',
  employer_partner: 'Employer Partner',
  provider_partner: 'Training Provider',
  youth_learner: 'Youth Learner',
};

const ROLE_HOME = {
  learner: '/dashboard',
  youth_learner: '/dashboard',
  facility_admin: '/admin',
  case_manager: '/case-manager',
  instructor: '/instructor',
  employer_partner: '/jobs/employer',
  provider_partner: '/marketplace/provider',
};

export default function FounderBar() {
  const { user, isImpersonating, impersonationRole, switchRole, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click — must be called unconditionally (Rules of Hooks)
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isSuperAdmin = user?.profile?.is_super_admin;

  // Auto-switch impersonation on arrival from /prototype (super admins only).
  useEffect(() => {
    if (!isSuperAdmin) return;
    let pending;
    try { pending = sessionStorage.getItem(PENDING_SWITCH_KEY); } catch (_e) { return; }
    if (pending !== '1') return;
    let canonical;
    try { canonical = localStorage.getItem(ACTIVE_ROLE_KEY); } catch (_e) { return; }
    if (!canonical) return;
    const target = PROTO_TO_WEB_ROLE[canonical];
    if (!target) return;
    if (target === impersonationRole) {
      try { sessionStorage.removeItem(PENDING_SWITCH_KEY); } catch (_e) { /* ignore */ }
      return;
    }
    (async () => {
      try {
        await switchRole(target);
        navigate(ROLE_HOME[target] || '/dashboard');
      } catch (e) {
        console.error('Auto role switch failed:', e);
      } finally {
        try { sessionStorage.removeItem(PENDING_SWITCH_KEY); } catch (_e) { /* ignore */ }
      }
    })();
  }, [isSuperAdmin, impersonationRole, switchRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore impersonation after a full page reload (state is in-memory only).
  const restoreAttemptedRef = useRef(false);
  useEffect(() => {
    if (!isSuperAdmin || isImpersonating || restoreAttemptedRef.current) return;
    let stored; let pending;
    try {
      stored = sessionStorage.getItem('aos_imp_role');
      pending = sessionStorage.getItem(PENDING_SWITCH_KEY);
    } catch (_e) { return; }
    if (!stored || pending === '1') return;
    restoreAttemptedRef.current = true;
    (async () => {
      try {
        await switchRole(stored);
      } catch (e) {
        console.error('Impersonation restore failed:', e);
        try { sessionStorage.removeItem('aos_imp_role'); } catch (_e) { /* ignore */ }
      }
    })();
  }, [isSuperAdmin, isImpersonating, switchRole]);

  if (!isSuperAdmin && !isImpersonating) return null;

  const handleSwitch = async (role) => {
    setOpen(false);
    if (loading) return;
    setLoading(true);
    try {
      await switchRole(role);
      navigate(ROLE_HOME[role] || '/dashboard');
    } catch (e) {
      console.error('Role switch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleExit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await endImpersonation();
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const RoleDropdown = ({ offset = 44 }) => (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        data-testid="role-switcher-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          background: isImpersonating ? 'rgba(0,0,0,0.15)' : 'rgba(249,115,22,0.1)',
          border: isImpersonating ? '1px solid rgba(0,0,0,0.2)' : '1px solid rgba(249,115,22,0.3)',
          borderRadius: '8px', padding: '6px 12px',
          color: isImpersonating ? '#111' : '#F97316',
          cursor: loading ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: '5px', minHeight: '32px',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          opacity: loading ? 0.6 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <Users size={12} aria-hidden="true" />
        {isImpersonating ? 'Switch Role' : 'Preview as Role'}
        <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Select a role to preview"
          style={{
            position: 'absolute', top: `${offset}px`, right: 0, zIndex: 660,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: '12px', padding: '6px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)', minWidth: '230px',
          }}
        >
          {ROLES.map(r => {
            const isCurrent = r.key === impersonationRole;
            return (
              <button
                key={r.key}
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSwitch(r.key)}
                data-testid={`role-option-${r.key}`}
                style={{
                  width: '100%', padding: '9px 12px', border: 'none',
                  background: isCurrent ? 'rgba(249,115,22,0.1)' : 'transparent',
                  color: isCurrent ? '#F97316' : 'var(--text-primary)',
                  borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '13px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontWeight: 600 }}>{r.label}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>{r.desc}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (isImpersonating) {
    // Full-width orange demo banner
    return (
      <div
        data-testid="demo-banner"
        role="status"
        aria-live="polite"
        style={{
          position: 'sticky', top: 0, zIndex: 650,
          background: '#F97316', color: '#111',
          padding: '0 20px', height: '44px', minHeight: '44px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 2px 12px rgba(249,115,22,0.4)',
          flexShrink: 0,
        }}
      >
        <span>
          Founder Demo &mdash; Viewing as{' '}
          <strong>{ROLE_LABELS[impersonationRole] || impersonationRole}</strong>
        </span>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <RoleDropdown offset={38} />
          <ToggleSeparator tone="dark" />
          <ViewToggle currentRole={impersonationRole || 'facility_admin'} source="dashboard" />
          <button
            onClick={handleExit}
            disabled={loading}
            data-testid="exit-demo-btn"
            style={{
              background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(0,0,0,0.25)',
              borderRadius: '8px', padding: '6px 12px',
              color: '#111', cursor: loading ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '5px', minHeight: '32px',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s',
            }}
          >
            <LogOut size={12} aria-hidden="true" /> Exit Demo
          </button>
        </div>
      </div>
    );
  }

  // Not impersonating — minimal founder indicator bar
  return (
    <div
      data-testid="founder-bar"
      style={{
        position: 'sticky', top: 0, zIndex: 650,
        background: 'rgba(249,115,22,0.04)',
        borderBottom: '1px solid rgba(249,115,22,0.12)',
        padding: '0 20px', height: '44px', minHeight: '44px',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        gap: '10px',
        flexShrink: 0,
      }}
    >
      <RoleDropdown offset={44} />
      <ToggleSeparator tone="dark" />
      <ViewToggle currentRole="learner" source="dashboard" />
    </div>
  );
}
